import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { runTask } from "@/lib/tasks";
import { openSSE } from "@/lib/sse";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, TrendingUp, TrendingDown, RefreshCcw, Radio, Square } from "lucide-react";
import { toast } from "sonner";

const Bullets = ({ items, tone = "default" }) => (
  <ul className="space-y-2">
    {(items || []).map((x, i) => (
      <li key={`${i}-${(x||"").slice(0,20)}`} className="text-sm leading-relaxed flex gap-2">
        <span className={`font-mono text-xs mt-1 ${tone === "good" ? "text-primary" : tone === "bad" ? "text-destructive" : "text-muted-foreground"}`}>▸</span>
        <span>{x}</span>
      </li>
    ))}
  </ul>
);

export default function Review() {
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);
  const streamBoxRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/api/performance/review/latest");
    setReview(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (streamBoxRef.current) streamBoxRef.current.scrollTop = streamBoxRef.current.scrollHeight;
  }, [streamText]);

  useEffect(() => () => { if (abortRef.current) abortRef.current(); }, []);

  const run = async () => {
    setBusy(true);
    toast.message("Running the AI Weekly Review…", { description: "Finding patterns in your metrics." });
    try {
      await runTask("/api/performance/review", null, { intervalMs: 2500, maxMs: 180000 });
      await load();
      toast.success("Review ready — next week's plan will incorporate this");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); }
    finally { setBusy(false); }
  };

  const startStream = () => {
    setStreamText("");
    setStreaming(true);
    abortRef.current = openSSE("/api/performance/review/stream", {
      onStart: () => setStreamText(""),
      onToken: ({ text }) => setStreamText((prev) => prev + (text || "")),
      onDone: () => {
        setStreaming(false);
        toast.success("Stream complete");
      },
      onError: (e) => {
        setStreaming(false);
        toast.error(e.message || "Stream failed");
      },
    });
  };

  const stopStream = () => {
    if (abortRef.current) abortRef.current();
    setStreaming(false);
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">AI Weekly Review</div>
            <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">What worked. What didn't. What's next.</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={run} disabled={busy || streaming} data-testid="btn-run-review">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin"/> Analyzing…</> : <><Sparkles className="h-4 w-4"/> {review ? "Rerun review" : "Run review"}</>}
            </Button>
            {!streaming ? (
              <Button variant="outline" onClick={startStream} disabled={busy} data-testid="btn-stream-review">
                <Radio className="h-4 w-4"/> Watch it think
              </Button>
            ) : (
              <Button variant="outline" onClick={stopStream} data-testid="btn-stop-stream">
                <Square className="h-4 w-4"/> Stop
              </Button>
            )}
          </div>
        </div>
      </div>

      {(streaming || streamText) && (
        <div className="px-8 pt-6">
          <div className="border border-primary rounded p-6 bg-primary/5" data-testid="review-stream">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-bold">
                <Radio className={`h-3.5 w-3.5 ${streaming ? "animate-pulse" : ""}`}/> {streaming ? "Streaming…" : "Stream complete"}
              </div>
              <div className="font-mono text-[10px] text-muted-foreground">{streamText.length} chars</div>
            </div>
            <pre
              ref={streamBoxRef}
              className="mt-3 max-h-[420px] overflow-auto whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-foreground"
            >{streamText}{streaming && <span className="inline-block w-2 h-4 bg-primary animate-pulse align-baseline ml-0.5"/>}</pre>
          </div>
        </div>
      )}

      {!review ? (
        <div className="p-10 text-muted-foreground max-w-2xl">
          Import at least a few posts under <span className="text-foreground font-semibold">Performance</span>, then run your first weekly review. ContentOS applies the <span className="text-foreground font-semibold">More / Better / New</span> philosophy to rewrite next week's plan.
        </div>
      ) : (
        <div className="p-8 grid grid-cols-12 gap-4">
          <div className="col-span-12 border border-border rounded p-6 bg-card" data-testid="review-summary">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Strategist summary</div>
            <div className="mt-2 text-lg leading-relaxed">{review.summary}</div>
          </div>

          <div className="col-span-12 border border-border rounded p-6 bg-card" data-testid="review-patterns">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Patterns detected</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(review.patterns || []).map((p, i) => (
                <div key={`pat-${i}-${(p||"").slice(0,20)}`} className="border border-border rounded p-4 bg-background">
                  <div className="font-mono text-xs text-primary">Pattern · {String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-1 text-sm leading-relaxed">{p}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-6 border border-border rounded p-6 bg-card" data-testid="review-worked">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
              <TrendingUp className="h-3.5 w-3.5 text-primary"/> What worked
            </div>
            <div className="mt-3"><Bullets items={review.what_worked} tone="good"/></div>
          </div>

          <div className="col-span-12 lg:col-span-6 border border-border rounded p-6 bg-card" data-testid="review-failed">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
              <TrendingDown className="h-3.5 w-3.5 text-destructive"/> What failed
            </div>
            <div className="mt-3"><Bullets items={review.what_failed} tone="bad"/></div>
          </div>

          <div className="col-span-12 border border-border rounded p-6 bg-card" data-testid="review-mbn">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">More / Better / New</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {["more","better","new"].map((k) => (
                <div key={k} className="border border-border rounded p-4 bg-background">
                  <div className={`font-mono text-xs uppercase tracking-widest ${k === "more" ? "text-primary" : k === "better" ? "text-foreground" : "text-destructive"}`}>{k}</div>
                  <div className="mt-2"><Bullets items={review.more_better_new?.[k]}/></div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-12 border border-primary rounded p-6 bg-primary/5" data-testid="review-nextweek">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-bold">
              <RefreshCcw className="h-3.5 w-3.5"/> Applied to next week
            </div>
            <div className="mt-3"><Bullets items={review.next_week_changes} tone="good"/></div>
            <div className="mt-4 text-xs text-muted-foreground">These changes will be woven into the next Weekly Plan generation.</div>
          </div>
        </div>
      )}
    </div>
  );
}
